import { internalFetch } from './fetchHelper';
import { getBackendApiKey, getBackendApiUrl } from './runtimeEnv';

export interface IWorkAssetFormRelationship {
  id: number;
  tenantId: number;
  workFormDefinitionId: number;
  workFormCode: string;
  workFormLabel: string;
  assetFormDefinitionId: number;
  assetFormCode: string;
  assetFormLabel: string;
  singularLabel: string | null;
  pluralLabel: string | null;
  publishedMetadataId: number | null;
  publishedVersion: number | null;
  publishedMetadataName: string | null;
}

export const ASSET_LABEL_MAX_LENGTH = 100;
export const normalizeAssetLabel = (label?: string | null): string | null =>
  label?.trim() || null;

const relationshipUrl = (
  endpoint: string,
  workFormDefinitionId?: number,
  tenantId?: number,
  assetFormDefinitionId?: number
) => {
  const query = new URLSearchParams({ code: getBackendApiKey() });
  if (workFormDefinitionId != null)
    query.set('workFormDefinitionId', String(workFormDefinitionId));
  if (tenantId != null) query.set('tenantId', String(tenantId));
  if (assetFormDefinitionId != null)
    query.set('assetFormDefinitionId', String(assetFormDefinitionId));
  return `${getBackendApiUrl()}/p/metadata/${endpoint}?${query}`;
};

export const getWorkAssetFormRelationships = async (
  controller: AbortController,
  workFormDefinitionId: number,
  tenantId: number,
  eligible = false
): Promise<IWorkAssetFormRelationship[]> => {
  const result = await internalFetch(
    relationshipUrl(
      eligible ? 'eligibleAssetForms' : 'workAssetFormRelationships',
      workFormDefinitionId,
      tenantId
    ),
    'GET',
    controller
  );
  if (
    !Array.isArray(result) ||
    result.some(
      (item) =>
        item == null ||
        item.workFormDefinitionId !== workFormDefinitionId ||
        item.tenantId !== tenantId ||
        !Number.isInteger(item.assetFormDefinitionId) ||
        item.assetFormDefinitionId <= 0
    )
  ) {
    throw new Error('The permitted Asset forms response was invalid.');
  }
  return result;
};

export const saveWorkAssetFormRelationship = (
  controller: AbortController,
  method: 'POST' | 'PUT',
  request: {
    workFormDefinitionId: number;
    assetFormDefinitionId: number;
    tenantId: number;
    singularLabel?: string | null;
    pluralLabel?: string | null;
  }
): Promise<IWorkAssetFormRelationship> =>
  internalFetch(
    relationshipUrl('workAssetFormRelationships'),
    method,
    controller,
    JSON.stringify({
      ...request,
      singularLabel: normalizeAssetLabel(request.singularLabel),
      pluralLabel: normalizeAssetLabel(request.pluralLabel),
    })
  );

export const deleteWorkAssetFormRelationship = async (
  controller: AbortController,
  workFormDefinitionId: number,
  assetFormDefinitionId: number,
  tenantId: number
): Promise<void> => {
  await internalFetch(
    relationshipUrl(
      'workAssetFormRelationships',
      workFormDefinitionId,
      tenantId,
      assetFormDefinitionId
    ),
    'DELETE',
    controller
  );
};

export const isAssetFormAllowed = (
  relationships: readonly IWorkAssetFormRelationship[],
  formDefinitionId?: number
): boolean =>
  relationships.length === 0 ||
  (formDefinitionId != null &&
    relationships.some(
      (item) => item.assetFormDefinitionId === formDefinitionId
    ));

export const assetRelationshipLabels = (
  relationships: readonly IWorkAssetFormRelationship[],
  formDefinitionId?: number
): { singular: string | undefined; plural: string | undefined } => {
  const relationship =
    formDefinitionId == null
      ? relationships.length === 1
        ? relationships[0]
        : undefined
      : relationships.find(
          (item) => item.assetFormDefinitionId === formDefinitionId
        );
  return {
    singular: normalizeAssetLabel(relationship?.singularLabel) ?? undefined,
    plural: normalizeAssetLabel(relationship?.pluralLabel) ?? undefined,
  };
};

export const assetSelectionIssues = (
  relationships: readonly IWorkAssetFormRelationship[],
  assetIds: readonly number[],
  creates: readonly { name: string; formDefinitionId?: number }[],
  imports: readonly { fileName: string; formDefinitionId?: number }[],
  knownAssetForms: Readonly<Record<number, number>> = {}
): string[] => {
  if (!relationships.length) return [];
  return [
    ...assetIds
      .filter((id) => !isAssetFormAllowed(relationships, knownAssetForms[id]))
      .map((id) =>
        knownAssetForms[id] == null
          ? `Asset #${id}: the Asset form identity could not be resolved. Remove or replace this selection.`
          : `Asset #${id}: this Asset form is not permitted. Remove or replace this selection.`
      ),
    ...creates
      .filter(
        (item) => !isAssetFormAllowed(relationships, item.formDefinitionId)
      )
      .map(
        (item) =>
          `${item.name}: this Asset form is not permitted or its identity is unavailable. Remove or replace this selection.`
      ),
    ...imports
      .filter(
        (item) => !isAssetFormAllowed(relationships, item.formDefinitionId)
      )
      .map(
        (item) =>
          `${item.fileName}: this import target form is not permitted or its identity is unavailable. Remove or replace this selection.`
      ),
  ];
};

/** Server-side restriction, applied before pagination and row counting. */
export const assetFormFilter = (
  formIds: readonly number[],
  tenantId?: number
): string | undefined => {
  const filters = [
    ...(formIds.length
      ? [
          {
            column: 'formDefinitionId',
            operation: 'In',
            value: `[${formIds.join(',')}]`,
            type: 'Number',
          },
        ]
      : []),
    ...(tenantId != null
      ? [
          {
            column: 'tenantId',
            operation: 'Eq',
            value: String(tenantId),
            type: 'Number',
          },
        ]
      : []),
  ];
  return filters.length ? JSON.stringify({ filters }) : undefined;
};
export const getAssetFormIdentities = async (
  controller: AbortController,
  assetIds: readonly number[],
  tenantId: number
): Promise<Record<number, number>> => {
  if (!assetIds.length) return {};
  const filters = JSON.stringify({
    filters: [
      {
        column: 'id',
        operation: 'In',
        value: `[${assetIds.join(',')}]`,
        type: 'Number',
      },
      {
        column: 'tenantId',
        operation: 'Eq',
        value: String(tenantId),
        type: 'Number',
      },
    ],
  });
  const identities: Record<number, number> = {};
  for (let page = 1; ; page++) {
    const query = new URLSearchParams({
      filters,
      allTenants: 'true',
      pageSize: '100',
      pageNo: String(page),
      code: getBackendApiKey(),
    });
    const result = await internalFetch(
      `${getBackendApiUrl()}/p/assets?${query}`,
      'GET',
      controller
    );
    for (const asset of result.items)
      if (
        Number.isInteger(asset.formDefinitionId) &&
        asset.formDefinitionId > 0
      )
        identities[asset.id] = asset.formDefinitionId;
    if (result.items.length < 100) return identities;
  }
};
